import { Card,  Row, Col, Typography, Alert, Space } from 'antd'; // Import Alert, Table, and Tag components
import { BsBatteryCharging, BsCheckCircle } from 'react-icons/bs';
import { FiAlertTriangle } from 'react-icons/fi';
import { useOperatorStore } from '../store/useOperatorStore';
import {
  // useDashboardLocationAnalysis,
  useGetDashboardCharger,
  useGetEnergyUtilizationGraph,
  useGetSessionsGraph,
} from '../Hooks/Index';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { useAuthStore } from '../store/useAuthStore';
import ChargerDashboard from '../components/ChargerDashboard';
import SessionsGraph from '../components/SessionsGraph';
import EnergyUtilizationGraph from '../components/EnergyUtilizationGraph';

const { Title, Text } = Typography;

const Dashboard = () => {
  const { t } = useTranslation(); // Initialize translation
  const { operatorId, name: operatorName } = useOperatorStore();
  const navigate = useNavigate(); // Initialize navigate
  const { user } = useAuthStore();

  const locationOwnerId = user?.role === 'LOCATION_OWNER' ? user?._id : null;

  const { data, isLoading } = useGetDashboardCharger(
    operatorId,
    locationOwnerId,
    {},
  );
  const { data: sessionsGraphData, isLoading: isLoadingSessionsGraph } = useGetSessionsGraph(
    operatorId,
    locationOwnerId,
  );
  const { data: energyUtilizationGraphData, isLoading: isLoadingEnergyUtilizationGraph } = useGetEnergyUtilizationGraph(
    operatorId,
    locationOwnerId,
  );

  const handleRedirect = (status, redirectToChargingHistory) => {
    if (redirectToChargingHistory) {
      navigate(`/dashboard/charging-history?status=InProgress`);
    } else {
      navigate(`/dashboard/chargers?status=${status}`);
    }
  };

  const stats = [
    {
      title: t('dashboard:stats.onlineChargers'),
      value: data?.onlineChargerCount,
      icon: <BsCheckCircle />,
      color: '#52c41a',
      status: 'ONLINE', // Add status for redirection
    },
    {
      title: t('dashboard:stats.faultedChargers'),
      value: data?.faultyChargerCount,
      icon: <FiAlertTriangle />,
      color: '#f5222d',
      status: 'FAULTED', // Add status for redirection
    },
    {
      title: t('dashboard:stats.offlineChargers'),
      value: data?.offlineChargerCount,
      icon: <FiAlertTriangle />,
      color: '#8c8c8c',
      status: 'OFFLINE', // Add status for redirection
    },
    {
      title: t('dashboard:stats.activeSessions'),
      value: data?.activeChargingCount,
      icon: <BsBatteryCharging />,
      color: '#1890ff',
      status: 'INPROGRESS', // Use INPROGRESS for active sessions
      redirectToChargingHistory: true, // Custom flag for charging history
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{width: '100%'}}>
      {operatorId &&
        (user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') && (
          <Alert
            message={t('dashboard:alertMessage', { operatorName })}
            type="info"
            showIcon
            closable
            style={{
              marginBottom: 24,
              border: 'none',
              backgroundColor: '#d7e1dd',
            }}
          />
        )}
      
      {/* Stats Cards - Improved responsive breakpoints */}
      <Row gutter={[12, 12]}>
        {stats.map((item) => {
          const isActiveSessions = item.redirectToChargingHistory;
          const isClickable = item.status && (!isActiveSessions || (isActiveSessions && item.value > 0));
          return (
            <Col xs={24} sm={12} md={6} lg={6} xl={6} key={item.title}>
              <Card
                isLoading={isLoading}
                style={{ 
                  borderLeft: `3px solid ${item.color}`,
                  padding: '12px',
                  height: '100%',
                  minHeight: '80px'
                }}
                onClick={isClickable ? () => handleRedirect(item.status, item.redirectToChargingHistory) : undefined}
                className={isClickable ? 'cursor-pointer !shadow-none border-none hover:shadow-md transition-shadow' : '!shadow-none border-none'}
                classNames={{ body: "!p-2" }}
              >
                <div className="flex items-center justify-between h-full">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span style={{ fontSize: '18px', color: item.color, flexShrink: 0 }}>
                      {item.icon}
                    </span>
                    <Text strong className="text-sm truncate leading-tight">{item.title}</Text>
                  </div>
                  <Title level={4} style={{ margin: 0, whiteSpace: 'nowrap', flexShrink: 0, fontSize: '1.25rem' }}>
                    {item.value}
                  </Title>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Graphs Section - Improved responsive layout */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
          {isLoadingSessionsGraph ? (
            <Card loading={true} style={{ minHeight: '350px' }} />
          ) : (
            <SessionsGraph
              data={sessionsGraphData}
              currentMonth={sessionsGraphData?.currentMonth}
              previousMonth={sessionsGraphData?.previousMonth}
              summary={sessionsGraphData?.summary}
            />
          )}
        </Col>
        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
          {isLoadingEnergyUtilizationGraph ? (
            <Card loading={true} style={{ minHeight: '350px' }} />
          ) : (
            <EnergyUtilizationGraph
              data={energyUtilizationGraphData}
              currentMonth={energyUtilizationGraphData?.currentMonth}
              previousMonth={energyUtilizationGraphData?.previousMonth}
              summary={energyUtilizationGraphData?.summary}
            />
          )}
        </Col>
      </Row>
      
      {/* Charger Dashboard - Full width on all screens */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
          <ChargerDashboard />
        </Col>
      </Row>
    </Space>
  );
};

export default Dashboard;
